import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';

import { SuiContainer, SuiButton, SuiField } from '@splashthat/sui';

import ImageUploader from 'common/compounds/ImageUploader';
import ImageCropper from 'common/compounds/ImageCropper';

import { baseBorderRadiusS, containerPaddingXs, buttonSmallPaddingS } from 'common/styles/variables';

const UPLOAD = 'Upload';
const EDIT_IMAGE = 'Edit Image';
const CROP = 'Crop';
const REMOVE = 'Remove';

const boxShadowOverlay = css`
    box-shadow: inset 0
        ${({ theme, height }) => `${height || '6rem'} ${transparentize(0.8, theme.palette.grayscale.black)}`};
`;

const ImagePreview = styled.img`
    flex: 1;
    background-image: url('https://d24wuq6o951i2g.cloudfront.net/img/transparent.png');
    border-radius: ${baseBorderRadiusS};
    background-repeat: repeat;
    background-size: auto;
    background-position: center center;

    height: ${(props) => props.height || '6rem'};
    object-fit: ${(props) => props.backgroundSize || 'cover'};
    ${(props) => props.src && boxShadowOverlay}
`;

const StyledSuiContainer = styled(SuiContainer)`
    padding: ${(props) => (props.dense ? containerPaddingXs : buttonSmallPaddingS)};

    padding-bottom: 0;
`;

const ButtonsContainer = styled(SuiContainer)`
    padding-bottom: 0.25rem;
`;

const baseClassName = 'ImagePicker';

export default class ImagePicker extends React.Component {
    static propTypes = {
        imageUrl: PropTypes.string,
        imageBaseUrl: PropTypes.string,
        onChangeImage: PropTypes.func.isRequired,
        onEditImage: PropTypes.func.isRequired,
        backgroundSize: PropTypes.string,
        qaId: PropTypes.string.isRequired,
        imagePreviewHeight: PropTypes.string,
        availableUploadMethods: PropTypes.array,
        dense: PropTypes.bool,
    };

    static defaultProps = {
        imageUrl: null,
        imageBaseUrl: null,
        backgroundSize: null,
        imagePreviewHeight: undefined,
        availableUploadMethods: ImageUploader.tabs,
        dense: false,
    };

    state = {
        isChangingImage: false,
        isCroppingImage: false,
    };

    toggleImageUploader = (value) => {
        this.setState((prevState) => ({
            ...prevState,
            isChangingImage: value,
        }));
    };

    toggleImageCropper = (value) => {
        this.setState((prevState) => ({
            ...prevState,
            isCroppingImage: value,
        }));
    };

    render() {
        const {
            imageUrl,
            imageBaseUrl,
            onChangeImage,
            onEditImage,
            qaId,
            backgroundSize,
            availableUploadMethods,
            dense,
        } = this.props;

        const openImageUploader = () => this.toggleImageUploader(true);
        const closeImageUploader = () => this.toggleImageUploader(false);
        const openImageCropper = () => this.toggleImageCropper(true);
        const closeImageCropper = () => this.toggleImageCropper(false);
        const resetImageUrls = (url = null) => {
            onChangeImage(url);
            onEditImage(url);
        };

        const uploadImageAndCloseUploader = (url) => {
            resetImageUrls(url);
            closeImageUploader();
        };

        const cropImageAndCloseCropper = (coords, url) => {
            onEditImage(url);
            closeImageCropper();
        };

        const BUTTONS = {
            [UPLOAD]: {
                visible: !imageUrl,
                handler: () => openImageUploader(),
            },
            [EDIT_IMAGE]: {
                visible: imageUrl,
                handler: () => openImageUploader(),
            },
            [CROP]: {
                visible: imageUrl,
                handler: () => openImageCropper(),
            },
            [REMOVE]: {
                visible: imageUrl,
                handler: () => resetImageUrls(),
            },
        };

        return (
            <SuiContainer>
                <SuiField
                    fullWidth
                    formElement={
                        <ImagePreview
                            className={`${baseClassName}__image`}
                            src={imageUrl}
                            backgroundSize={backgroundSize}
                            onClick={openImageUploader}
                            data-qa={`${qaId}--preview`}
                            height={this.props.imagePreviewHeight}
                            alt={imageUrl && 'Image Preview'}
                        />
                    }
                />
                <ButtonsContainer className={`${baseClassName}__buttons`} variation="flex-horizontal-centered">
                    {Object.keys(BUTTONS).map((button) =>
                        BUTTONS[button].visible ? (
                            <StyledSuiContainer
                                key={button}
                                className={`${baseClassName}__image-options`}
                                dense={dense}
                                data-qa={`${qaId}--button-${button}`}
                            >
                                <SuiButton
                                    className={`${baseClassName}__image-label`}
                                    label={button}
                                    onClick={() => BUTTONS[button].handler()}
                                    variation="text"
                                />
                            </StyledSuiContainer>
                        ) : null,
                    )}
                </ButtonsContainer>
                <ImageUploader
                    open={this.state.isChangingImage}
                    onImageSelected={uploadImageAndCloseUploader}
                    closeImageUploader={closeImageUploader}
                    availableTabs={availableUploadMethods}
                />
                <ImageCropper
                    open={this.state.isCroppingImage}
                    handleCropping
                    keepOpenWhileCropping
                    imageUrl={imageBaseUrl}
                    onCrop={cropImageAndCloseCropper}
                    onCancel={closeImageCropper}
                />
            </SuiContainer>
        );
    }
}
